import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const useSkillManager = ({
    selectedCard,
    cardSkills,
    selectedStars,
    setUniqueSkill,
    isRestoring,
    setUniqueSkillLevel,
    skillsRestored,
    potentialLevel,
    setActiveSkills,
    activeSkills,
    allSkills,
    setNotification,
    characters,
    setSelectedCharacter,
    cards,
    setSelectedCard,
    setSelectedStars,
    setAptitudes,
    setEditableStats
}) => {

    // Logic to update Unique Skill and Active Skills based on configuration
    const { t } = useTranslation();

    useEffect(() => {
        if (!selectedCard || cardSkills.length === 0) return;

        // 1. Determine Unique Skill
        let targetUnique = null;
        const uniqueSkills = cardSkills.filter(s => s.Category === 'Unique');

        if (selectedStars >= 3) {
            targetUnique = uniqueSkills.find(s => s.star === 3) || uniqueSkills.find(s => s.star === null); // Fallback
        } else {
            targetUnique = uniqueSkills.find(s => s.star < 3) || uniqueSkills.find(s => s.star === 1) || uniqueSkills.find(s => s.star === 2);
        }
        setUniqueSkill(targetUnique);

        // Auto-set Unique Skill Level based on Stars
        if (!isRestoring.current) {
            if (selectedStars === 4) {
                setUniqueSkillLevel(2);
            } else if (selectedStars === 5) {
                setUniqueSkillLevel(3);
            } else {
                setUniqueSkillLevel(1);
            }
        }

        // 2. Update Active Skills (Innate & Awakening)
        if (!skillsRestored.current) {
            const rawInnate = cardSkills.filter(s => s.Category === 'Innate');
            const rawAwakening = cardSkills.filter(s => s.Category === 'Awakening' && s.star <= potentialLevel);

            setActiveSkills(prev => {
                const existingStateMap = new Map();
                prev.forEach(item => {
                    const id = item.skill.Id || item.skill.skill_id;
                    existingStateMap.set(id, { active: item.active, level: item.level, fastLearner: item.fastLearner });
                });

                const createSkillItem = (skill, source) => {
                    const id = skill.Id || skill.skill_id;
                    const existingState = existingStateMap.get(id);
                    return {
                        skill: skill,
                        level: existingState ? existingState.level : 0,
                        active: existingState ? existingState.active : false,
                        isAuto: true,
                        source: source,
                        fastLearner: existingState ? existingState.fastLearner : false
                    };
                };

                const newInnateSkills = rawInnate.map(s => createSkillItem(s, 'Innate'));
                const newAwakeningSkills = rawAwakening.map(s => createSkillItem(s, 'Awakening'));

                const autoIds = new Set([
                    ...newInnateSkills.map(i => i.skill.Id || i.skill.skill_id),
                    ...newAwakeningSkills.map(i => i.skill.Id || i.skill.skill_id)
                ]);

                const manualSkills = prev.filter(item => !item.isAuto);
                const filteredManual = manualSkills.filter(item => {
                    const skillId = item.skill.Id || item.skill.skill_id;
                    return !autoIds.has(skillId);
                });

                const combinedSkills = [...newInnateSkills, ...newAwakeningSkills, ...filteredManual];

                // Fix: Remove "Orphaned" Upgrades.
                // If an Upgrade skill exists but its Base skill is no longer in the list (e.g. because Awakening level dropped),
                // the Upgrade must also be removed.
                const activeIdSkills = new Set(combinedSkills.map(s => parseInt(s.skill.Id_skill, 10)));

                const validSkills = combinedSkills.filter(item => {
                    // If it has no base_skill_id, it's a Base skill (or independent). Keep it.
                    if (!item.skill.base_skill_id) return true;

                    // If it has a base_skill_id, check if that Base exists in the NEW list.
                    const baseId = parseInt(item.skill.base_skill_id, 10);
                    return activeIdSkills.has(baseId);
                });

                return validSkills;
            });
        }

        if (isRestoring.current) {
            // Timeout handled in onLoad
        }

    }, [cardSkills, selectedStars, potentialLevel, selectedCard, isRestoring, skillsRestored, setUniqueSkill, setUniqueSkillLevel, setActiveSkills]);

    const handleCharacterChange = useCallback((personajeId) => {
        const newSelectedCharacter = characters.find(c => c.Id_Personaje === parseInt(personajeId, 10));
        setSelectedCharacter(newSelectedCharacter);
    }, [characters, setSelectedCharacter]);

    const handleCostumeChange = useCallback((cardId) => {
        const newSelectedCard = cards.find(c => c.card_id === parseInt(cardId, 10));
        setSelectedCard(newSelectedCard);
    }, [cards, setSelectedCard]);

    const handleStarsChange = useCallback((stars) => {
        setSelectedStars(parseInt(stars, 10));
    }, [setSelectedStars]);

    const handleAptitudeChange = useCallback((aptKey, newRank) => {
        setAptitudes(prev => ({
            ...prev,
            [aptKey]: newRank
        }));
    }, [setAptitudes]);

    const handleStatChange = useCallback((statName, newValue) => {
        const numericValue = parseInt(newValue, 10) || 0;
        setEditableStats(prev => ({
            ...prev,
            [statName]: numericValue
        }));
    }, [setEditableStats]);

    // Skill Panel Handlers
    const handleAddSkill = useCallback((skill, level = 0, baseLevel = 0) => {
        // Use functional state update to ensure we work with latest state
        setActiveSkills(prev => {
            const updatedSkills = [...prev];

            // Check if the target skill already exists to decide between update or add
            const existingSkillIndex = updatedSkills.findIndex(item => item.skill.Id === skill.Id);

            // 1. Handle Base Skill Logic (Sync Base Level)
            // Even if we are just updating the main skill, we might also want to update the base level
            if (skill.base_skill_id) {
                const baseSkillId = parseInt(skill.base_skill_id, 10);
                const existingBaseIndex = updatedSkills.findIndex(item => parseInt(item.skill.Id_skill, 10) === baseSkillId);

                if (existingBaseIndex !== -1) {
                    // CASE A: Base Skill EXISTS -> Update its level
                    const baseItem = updatedSkills[existingBaseIndex];
                    if (!baseItem.skill.color || !baseItem.skill.color.includes('Purple')) {
                        updatedSkills[existingBaseIndex] = {
                            ...baseItem,
                            level: baseLevel // Sync Base Level
                        };
                    }
                } else {
                    // CASE B: Base Skill MISSING -> Add it (Only if adding new upgrade or forcing check)
                    const baseSkill = allSkills.find(s => parseInt(s.Id_skill, 10) === baseSkillId);
                    if (baseSkill) {
                        if (!baseSkill.color || !baseSkill.color.includes('Purple')) {
                            updatedSkills.push({
                                skill: baseSkill,
                                level: baseLevel,
                                active: false,
                                isAuto: false
                            });
                        }
                    }
                }
            }

            // 2. Add or Update the Target Skill
            if (existingSkillIndex !== -1) {
                // UPDATE existing skill
                updatedSkills[existingSkillIndex] = {
                    ...updatedSkills[existingSkillIndex],
                    level: level,
                    // We don't change 'active' or 'isAuto' state on level change usually, 
                    // providing a seamless "Edit" experience.
                };
            } else {
                // ADD new skill
                updatedSkills.push({
                    skill: skill,
                    level: level,
                    active: false,
                    isAuto: false,
                    fastLearner: false
                });
            }

            return updatedSkills;
        });
    }, [setActiveSkills, allSkills]);

    const handleRemoveSkill = useCallback((skillId) => {
        const targetItem = activeSkills.find(item => item.skill.Id === skillId);
        if (!targetItem) return;

        const showNotification = (msg) => {
            setNotification(msg);
            setTimeout(() => setNotification(null), 3000);
        };

        if (targetItem.isAuto || targetItem.source === 'Innate' || targetItem.source === 'Awakening') {
            showNotification(t('SkillManager.error_remove_auto', { source: targetItem.source || 'Auto', skillName: targetItem.skill.Skill_name }));
            return;
        }

        const idsToRemove = new Set([skillId]);

        const upgradeItem = activeSkills.find(item =>
            item.skill.base_skill_id && parseInt(item.skill.base_skill_id, 10) === parseInt(targetItem.skill.Id_skill, 10)
        );

        if (upgradeItem) {
            if (upgradeItem.isAuto || upgradeItem.source === 'Innate' || upgradeItem.source === 'Awakening') {
                showNotification(t('SkillManager.error_remove_upgrade', { upgradeName: upgradeItem.skill.Skill_name, source: upgradeItem.source || 'Auto' }));
                return;
            }
            idsToRemove.add(upgradeItem.skill.Id);
        }

        setActiveSkills(prev => prev.filter(item => !idsToRemove.has(item.skill.Id)));
    }, [activeSkills, setActiveSkills, setNotification, t]);

    const handleToggleSkill = useCallback((skillId) => {
        setActiveSkills(prev => {
            const targetItem = prev.find(item => item.skill.Id === skillId);
            if (!targetItem) return prev;

            if (targetItem.active) {
                const idsToDeactivate = new Set([skillId]);

                const findUpgradeRecursively = (currentSkill) => {
                    const upgradeItem = prev.find(item =>
                        item.skill.base_skill_id && parseInt(item.skill.base_skill_id, 10) === parseInt(currentSkill.Id_skill, 10)
                    );

                    if (upgradeItem) {
                        if (upgradeItem.skill.color && !upgradeItem.skill.color.includes('Purple')) {
                            idsToDeactivate.add(upgradeItem.skill.Id);
                            findUpgradeRecursively(upgradeItem.skill);
                        }
                    }
                };

                findUpgradeRecursively(targetItem.skill);

                return prev.map(item =>
                    idsToDeactivate.has(item.skill.Id) ? { ...item, active: false } : item
                );
            }

            const idsToActivate = new Set([skillId]);

            const findBaseRecursively = (currentSkill) => {
                if (!currentSkill.base_skill_id) return;

                const baseItem = prev.find(item => parseInt(item.skill.Id_skill, 10) === parseInt(currentSkill.base_skill_id, 10));

                if (baseItem) {
                    if (baseItem.skill.color && !baseItem.skill.color.includes('Purple')) {
                        idsToActivate.add(baseItem.skill.Id);
                        findBaseRecursively(baseItem.skill);
                    }
                }
            };

            findBaseRecursively(targetItem.skill);

            return prev.map(item =>
                idsToActivate.has(item.skill.Id) ? { ...item, active: true } : item
            );
        });
    }, [setActiveSkills]);

    const handleLevelChange = useCallback((skillId, newLevel) => {
        setActiveSkills(prev => prev.map(item =>
            item.skill.Id === skillId ? { ...item, level: newLevel } : item
        ));
    }, [setActiveSkills]);

    const handleToggleFastLearner = useCallback((skillId) => {
        setActiveSkills(prev => prev.map(item =>
            item.skill.Id === skillId ? { ...item, fastLearner: !item.fastLearner } : item
        ));
    }, [setActiveSkills]);

    const handleUpgradeSkill = useCallback((baseSkillId) => {
        const baseItem = activeSkills.find(item => item.skill.Id === baseSkillId);
        if (!baseItem || !baseItem.skill.upgrade_id) return;

        const upgradeSkill = allSkills.find(s => parseInt(s.Id_skill, 10) === parseInt(baseItem.skill.upgrade_id, 10));

        if (upgradeSkill) {
            handleAddSkill(upgradeSkill);
        }
    }, [activeSkills, allSkills, handleAddSkill]);

    const handleDeactivateAllSkills = useCallback(() => {
        setActiveSkills(prev => prev.map(item => {
            return { ...item, active: false };
        }));
    }, [setActiveSkills]);

    const handleToggleIgnoreCost = useCallback((skillId) => {
        setActiveSkills(prev => {
            const targetItem = prev.find(item => item.skill.Id === skillId);
            if (!targetItem) return prev;

            const newIgnoreState = !targetItem.ignoreCost;
            const idsToUpdate = new Set([skillId]);

            // Cascade: If marking Upgrade as Purchased (ignoreCost = true),
            // automatically mark Base as Purchased too.
            if (newIgnoreState && targetItem.skill.base_skill_id) {
                const baseItem = prev.find(item => parseInt(item.skill.Id_skill, 10) === parseInt(targetItem.skill.base_skill_id, 10));
                if (baseItem) {
                    idsToUpdate.add(baseItem.skill.Id);
                }
            }

            return prev.map(item => {
                if (idsToUpdate.has(item.skill.Id)) {
                    // If we are cascading to Base, we force it to TRUE (newIgnoreState).
                    // If we are just toggling the target, we use newIgnoreState.
                    return { ...item, ignoreCost: newIgnoreState };
                }
                return item;
            });
        });
    }, [setActiveSkills]);

    const handleToggleAllFastLearner = useCallback(() => {
        setActiveSkills(prev => {
            // Check if ALL skills currently have fastLearner = true
            // If yes, turn them all OFF. If no (some are off), turn them all ON.
            const allAreFast = prev.length > 0 && prev.every(item => item.fastLearner);
            const newState = !allAreFast;

            return prev.map(item => ({
                ...item,
                fastLearner: newState
            }));
        });
    }, [setActiveSkills]);

    const handleCleanSkills = useCallback(() => {
        setActiveSkills(prev => prev.filter(item =>
            item.source === 'Innate' || item.source === 'Awakening' || item.isAuto
        ));
    }, [setActiveSkills]);

    return {
        handleCharacterChange,
        handleCostumeChange,
        handleStarsChange,
        handleAptitudeChange,
        handleStatChange,
        handleAddSkill,
        handleRemoveSkill,
        handleToggleSkill,
        handleLevelChange,
        handleToggleFastLearner,
        handleUpgradeSkill,
        handleDeactivateAllSkills,
        handleToggleIgnoreCost,
        handleToggleAllFastLearner,
        handleCleanSkills
    };
};

export default useSkillManager;
// Revisado: 2025-12-16 - Optimized with useCallback
